import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Send, Phone, Mail as MailIcon, CheckCircle } from 'lucide-react';
import { getTours, submitGroupInquiry } from '../services/api';
import SEO from './SEO';

const perks = [
  { title: 'Özel Grup Fiyatı', desc: '4 kişiden itibaren indirimli teklif hazırlıyoruz.' },
  { title: 'Kendi Tarihiniz', desc: 'Grubunuza uygun tarihte özel çıkış planlıyoruz.' },
  { title: 'Kişiselleştirilmiş Program', desc: 'Ekstra dinlenme, farklı zorluk, özel diyet — her şey mümkün.' },
  { title: 'Tek İletişim Noktası', desc: 'Rehberiniz gruptan sorumlu, tüm koordinasyon sizden geçer.' },
];

const GroupInquiryPage = () => {
  const [tours, setTours] = useState([]);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', groupSize: 4, tour: '', dates: '', message: '',
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    getTours({ limit: 50 })
      .then((r) => setTours((r.data || []).filter((t) => t.active !== false)))
      .catch(() => setTours([]));
    window.scrollTo(0, 0);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (form.groupSize < 4) { setError('Grup rezervasyonu için en az 4 kişi gerekli.'); return; }
    if (!form.name.trim() || !form.email.includes('@') || !form.phone.trim()) {
      setError('İsim, e-posta ve telefon zorunludur.'); return;
    }
    setSending(true);
    try {
      const res = await submitGroupInquiry(form);
      setSuccess(res.data?.message || 'Talebiniz alındı.');
      setForm({ name: '', email: '', phone: '', groupSize: 4, tour: '', dates: '', message: '' });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Talep gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSending(false);
    }
  };

  const inp = 'w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#C8D843] bg-white';
  const lbl = 'block text-xs font-semibold mb-1.5 text-gray-700';

  return (
    <section className="bg-white min-h-screen" data-testid="group-inquiry-page">
      <SEO
        title="Grup Rezervasyonu — 4+ Kişilik Özel Turlar"
        description="Aile, arkadaş grubu veya şirket için 4 kişiden itibaren indirimli özel trekking turları. Kendi tarihinizde kokartlı rehberle Likya Yolu, Karya Yolu."
        url="https://camption.com/grup-rezervasyonu"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
        <nav aria-label="Breadcrumb" className="text-xs text-gray-500 mb-4">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to="/" className="hover:text-[#3D4A26]">Ana Sayfa</Link></li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-700">Grup Rezervasyonu</li>
          </ol>
        </nav>

        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-2 flex items-center gap-2">
          <Users size={13} /> 4+ Kişi
        </p>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1B1B1B] leading-tight mb-4" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          Grubunuza özel çıkış
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mb-10">
          Aile, arkadaş grubu ya da şirket ekibiyle mi geliyorsunuz? Size özel tarih, özel fiyat ve isteğe uyarlanmış program hazırlayalım. Formu doldurun, 24 saat içinde size teklifle dönelim.
        </p>

        {/* Perks */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
          {perks.map((p) => (
            <div key={p.title} className="flex gap-3 bg-[#FAFAF7] rounded-xl p-4 border border-gray-100">
              <CheckCircle size={20} className="text-[#3D4A26] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-[#1B1B1B] text-sm">{p.title}</p>
                <p className="text-xs text-gray-600 leading-relaxed mt-0.5">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={submit} className="bg-[#FAFAF7] border border-gray-200 rounded-2xl p-6 md:p-8 space-y-5" data-testid="group-inquiry-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Ad Soyad *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} maxLength={120} required data-testid="group-name-input" />
            </div>
            <div>
              <label className={lbl}>Grup Boyutu *</label>
              <input
                type="number"
                min={4}
                max={200}
                value={form.groupSize}
                onChange={(e) => setForm({ ...form, groupSize: Number(e.target.value) })}
                className={inp}
                required
                data-testid="group-size-input"
              />
              <p className="text-[11px] text-gray-500 mt-1">Minimum 4 kişi</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={lbl}>E-posta *</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} maxLength={200} required data-testid="group-email-input" />
            </div>
            <div>
              <label className={lbl}>Telefon *</label>
              <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} maxLength={40} required data-testid="group-phone-input" placeholder="+90 5xx xxx xx xx" />
            </div>
          </div>

          <div>
            <label className={lbl}>Tercih Edilen Tur (opsiyonel)</label>
            <select value={form.tour} onChange={(e) => setForm({ ...form, tour: e.target.value })} className={inp} data-testid="group-tour-select">
              <option value="">Farketmez / Öneri istiyorum</option>
              {tours.map((t) => (
                <option key={t.id} value={t.title}>{t.title}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={lbl}>Tercih Edilen Tarih Aralığı (opsiyonel)</label>
            <input value={form.dates} onChange={(e) => setForm({ ...form, dates: e.target.value })} className={inp} maxLength={200} placeholder="Örn: Nisan 2026, ya da 3 Mayıs civarı" data-testid="group-dates-input" />
          </div>

          <div>
            <label className={lbl}>Ek Notlar (opsiyonel)</label>
            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className={`${inp} resize-none`} maxLength={5000} placeholder="Diyet kısıtlamaları, deneyim seviyesi, kutlama gibi özel istekleriniz varsa yazabilirsiniz." data-testid="group-message-input" />
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
          {success && <p className="text-sm text-[#3D4A26] bg-[#f0f4d8] border border-[#C8D843]/30 rounded-lg px-3 py-2" data-testid="group-inquiry-success">{success}</p>}

          <button
            type="submit"
            disabled={sending}
            data-testid="group-inquiry-submit"
            className="w-full bg-[#3D4A26] text-white font-bold py-3.5 rounded-lg hover:bg-[#2d3a1a] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {sending ? 'Gönderiliyor...' : (<><Send size={16} /> Teklif İste</>)}
          </button>

          <div className="text-center text-xs text-gray-500 pt-2 border-t border-gray-200 space-y-1">
            <p>Hemen bize ulaşmak isterseniz:</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <a href="tel:+908504959524" className="inline-flex items-center gap-1 hover:text-[#3D4A26] font-semibold"><Phone size={11} /> 0850 495 95 24</a>
              <a href="mailto:hello@camption.com" className="inline-flex items-center gap-1 hover:text-[#3D4A26] font-semibold"><MailIcon size={11} /> hello@camption.com</a>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default GroupInquiryPage;
